import { useAtomValue, useSetAtom } from 'jotai';

import {
  CONNECTION_STATUS,
  chronicleConnectionContextAtom,
  chronicleDeviceStateAtom,
  isChronicleConnectedAtom,
  usbConnectedAtom,
} from '@/atoms/chronicle';
import { ToggleGroup, ToggleGroupItem } from '@heidi/design-system/toggle-group';
import { TypographyCaption, TypographyP2SemiBold } from '@heidi/design-system/typography';

import { remoteSimAppliedAtom } from './heidi-remote-dev-atoms';

// Writes the Chronicle atoms (disconnected by default on web) to simulate a
// device; the sidebar entry reacts live and resets on reload (in-memory). Sets
// only linkedDeviceId/connectionStatus/USB, never deviceName/serialNumber/
// firmwareVersion — useSyncHardwareProfileMetadata would egress those.
//
// Gotcha: Bluetooth (→CONNECTED) also flips chronicleIsInputAtom via
// useChronicleAutoSelectInput, so a paused browser recording's Stop routes to
// Chronicle — real-connect behaviour, not a stop bug.

type ScenarioKey = 'unpaired' | 'disconnected' | 'bluetooth' | 'usb';

const SCENARIOS: { key: ScenarioKey; label: string }[] = [
  { key: 'unpaired', label: 'Unpaired' },
  { key: 'disconnected', label: 'Disconnected' },
  { key: 'bluetooth', label: 'Bluetooth' },
  { key: 'usb', label: 'USB' },
];

export function HeidiRemoteDevSection() {
  const setContext = useSetAtom(chronicleConnectionContextAtom);
  const setDeviceState = useSetAtom(chronicleDeviceStateAtom);
  const setUsbConnected = useSetAtom(usbConnectedAtom);
  const setSimApplied = useSetAtom(remoteSimAppliedAtom);

  const isConnected = useAtomValue(isChronicleConnectedAtom);
  const isUsb = useAtomValue(usbConnectedAtom);
  const { linkedDeviceId } = useAtomValue(chronicleConnectionContextAtom);

  // Reflect the real atom state so the highlighted button doubles as an inspector.
  const activeKey: ScenarioKey = !linkedDeviceId
    ? 'unpaired'
    : isUsb && !isConnected
      ? 'usb'
      : isConnected
        ? 'bluetooth'
        : 'disconnected';

  const applyScenario = (key: ScenarioKey) => {
    setContext((prev) => ({
      ...prev,
      linkedDeviceId: key === 'unpaired' ? null : 'dev-remote-1',
      isLinkingDevice: false,
    }));
    setDeviceState((prev) => ({
      ...prev,
      connectionStatus:
        key === 'bluetooth'
          ? CONNECTION_STATUS.CONNECTED
          : CONNECTION_STATUS.DISCONNECTED,
      usbModeEnabled: key === 'usb',
    }));
    setUsbConnected(key === 'usb');
    setSimApplied(true);
  };

  return (
    <section aria-labelledby="dev-remote-connection-heading" className="mt-4">
      <TypographyP2SemiBold id="dev-remote-connection-heading" className="mb-1">
        Device connection state
      </TypographyP2SemiBold>
      <TypographyCaption className="mb-3 text-text-tertiary">
        Simulate the device state. The sidebar Remote entry updates live.
      </TypographyCaption>

      <ToggleGroup
        type="single"
        value={activeKey}
        onValueChange={(next) => {
          if (next) applyScenario(next as ScenarioKey);
        }}
        className="flex-wrap justify-start"
      >
        {SCENARIOS.map(({ key, label }) => (
          <ToggleGroupItem key={key} value={key} variant="outline" size="sm">
            {label}
          </ToggleGroupItem>
        ))}
      </ToggleGroup>

      <div className="mt-3 flex flex-col gap-0.5">
        {(
          [
            ['linked', Boolean(linkedDeviceId)],
            ['BLE', isConnected],
            ['USB', isUsb],
          ] as const
        ).map(([label, value]) => (
          <TypographyCaption key={label} className="text-text-secondary">
            {label}: {String(value)}
          </TypographyCaption>
        ))}
      </div>
    </section>
  );
}
